import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { BRAND } from "../../lib/brand";
import { SKILL_GROUPS } from "../../data/profileData";

const ACCENTS = [BRAND.red, BRAND.blue, BRAND.teal, BRAND.green];

/**
 * Capabilities.
 *
 * From `lg` up this is the four-column grid the design calls for: an accent
 * rule, a title, and a plain list under each.
 *
 * Below that they stack into one or two flat lists running down the page — every
 * line the same weight, no edges, nothing to hold on to. So below `lg` each
 * group becomes a card instead: a big ghost numeral in the
 * corner, an accent rule, and the items as wrapping chips, which turns the same
 * content into compact blocks that read at a glance. Same DOM, same words —
 * the difference is entirely in the responsive classes.
 */
export function SkillsSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="relative w-full px-4 md:px-[116px] py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">Capabilities</p>
          <h2 className="font-black text-[36px] md:text-[64px] leading-[1.05] tracking-tight text-[#141414]">
            What I bring to the team
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:gap-8 lg:grid-cols-4">
          {SKILL_GROUPS.map((group, i) => {
            const accent = ACCENTS[i % ACCENTS.length];
            return (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                className="relative flex flex-col gap-3 overflow-hidden rounded-2xl bg-white/70 p-5 shadow-[0_18px_36px_-26px_rgba(0,62,91,0.55)] lg:overflow-visible lg:rounded-none lg:bg-transparent lg:p-0 lg:shadow-none"
              >
                {/* Ghost numeral — the card's only decoration, and the thing that
                    tells you at a glance which of the four you are looking at. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-3 right-2 select-none font-black text-[68px] leading-none text-[#141414]/[0.05] lg:hidden"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>

                <span className="h-[6px] w-[40px] rounded-full" style={{ background: accent }} aria-hidden="true" />
                <h3 className="relative font-bold text-[18px] leading-tight text-[#141414]">{group.title}</h3>
                <ul className="relative flex flex-row flex-wrap gap-2 lg:flex-col lg:gap-1.5">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      // Chips below `sm`, plain list rows from `sm` up. The chip
                      // border is tinted with the group's accent so the four
                      // blocks stay visually distinct once stacked.
                      className="rounded-full border bg-white/80 px-3 py-1.5 text-[13px] leading-[1.3] text-[#141414] lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:text-[15px] lg:leading-[1.5] lg:text-[#666]"
                      style={{ borderColor: `${accent}59` }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
